import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, Search, X, Loader2, ScanSearch } from "lucide-react";

/**
 * Admin-only: batch verify unverified businesses whose text fields contain
 * community keywords ("black owned", "african", "ados", …).
 *
 * Flow:
 *   1. Preview: POST /admin/businesses/keyword-match with keywords → list of matches
 *   2. Admin unticks any false-positives in the preview table
 *   3. Confirm: POST /admin/businesses/bulk-verify with the surviving IDs
 */

const DEFAULT_KEYWORDS = [
  "black owned",
  "black-owned",
  "african american",
  "african-american",
  "african",
  "ados",
];

export default function AdminBatchVerifyByKeywordsDialog({ open, onOpenChange, onVerified }) {
  const [keywords, setKeywords] = useState([...DEFAULT_KEYWORDS]);
  const [customInput, setCustomInput] = useState("");
  const [matches, setMatches] = useState(null); // null = not previewed yet
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const reset = () => {
    setKeywords([...DEFAULT_KEYWORDS]);
    setCustomInput("");
    setMatches(null);
    setSelected(new Set());
    setLoading(false);
    setVerifying(false);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const addKeyword = () => {
    const k = customInput.trim();
    if (!k) return;
    if (keywords.some((x) => x.toLowerCase() === k.toLowerCase())) {
      toast.info("Already in the list");
      return;
    }
    setKeywords([...keywords, k]);
    setCustomInput("");
    setMatches(null); // invalidate preview
  };

  const removeKeyword = (k) => {
    setKeywords(keywords.filter((x) => x !== k));
    setMatches(null);
  };

  const runPreview = async () => {
    if (!keywords.length) {
      toast.error("Add at least one keyword");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/admin/businesses/keyword-match`,
        { keywords, only_unverified: true, limit: 500 },
        { withCredentials: true },
      );
      const list = res.data?.matches || [];
      setMatches(list);
      setSelected(new Set(list.map((m) => m.business_id)));
      if (!list.length) toast.info("No unverified businesses match those keywords.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (!matches) return;
    if (selected.size === matches.length) setSelected(new Set());
    else setSelected(new Set(matches.map((m) => m.business_id)));
  };

  const confirmVerify = async () => {
    if (!selected.size) {
      toast.error("Nothing selected to verify");
      return;
    }
    setVerifying(true);
    try {
      const res = await axios.post(
        `${API}/admin/businesses/bulk-verify`,
        { business_ids: Array.from(selected), is_verified: true },
        { withCredentials: true },
      );
      const n = res.data?.modified_count ?? 0;
      toast.success(`Verified ${n} business${n === 1 ? "" : "es"} by keyword match.`);
      onVerified?.(n);
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk verify failed");
    } finally {
      setVerifying(false);
    }
  };

  const snippet = (m) => {
    const raw = (m.description || m.bio || "").trim();
    if (!raw) return null;
    return raw.length > 160 ? raw.slice(0, 160) + "…" : raw;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="batch-verify-by-keyword-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-tbc-navy">
            <ScanSearch className="w-5 h-5 text-tbc-gold" />
            Batch verify by keyword
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2 pr-1">
          {/* Keyword chips */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">
              Verify unverified businesses whose name, description, bio, or services contain any of:
            </p>
            <div className="flex flex-wrap gap-2 mb-2">
              {keywords.map((k) => (
                <span
                  key={k}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-tbc-cream/60 text-tbc-navy text-xs font-medium border border-tbc-gold/40"
                  data-testid={`keyword-chip-${k}`}
                >
                  {k}
                  <button
                    type="button"
                    onClick={() => removeKeyword(k)}
                    className="hover:text-red-600"
                    aria-label={`Remove keyword ${k}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {!keywords.length && (
                <span className="text-xs text-gray-400">No keywords — add one below.</span>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                type="text"
                value={customInput}
                placeholder="Add another keyword (e.g. HBCU, diaspora, minority-owned)"
                onChange={(e) => setCustomInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addKeyword();
                  }
                }}
                data-testid="batch-verify-add-keyword-input"
                className="text-sm"
              />
              <Button
                type="button"
                onClick={addKeyword}
                variant="outline"
                disabled={!customInput.trim()}
                data-testid="batch-verify-add-keyword-btn"
              >
                Add
              </Button>
            </div>
          </div>

          {/* Preview / Match list */}
          {matches === null ? (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg text-sm">
              Click <strong>Preview matches</strong> to see which businesses will be verified.
            </div>
          ) : matches.length === 0 ? (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg text-sm">
              No unverified businesses matched — all clear.
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs">
                <button
                  type="button"
                  onClick={toggleAll}
                  className="text-tbc-navy hover:underline font-medium"
                  data-testid="batch-verify-toggle-all"
                >
                  {selected.size === matches.length ? "Deselect all" : "Select all"}
                </button>
                <span className="text-gray-500" data-testid="batch-verify-selection-count">
                  {selected.size} of {matches.length} selected
                </span>
              </div>
              <div className="max-h-[40vh] overflow-y-auto divide-y divide-gray-100">
                {matches.map((m) => {
                  const checked = selected.has(m.business_id);
                  return (
                    <label
                      key={m.business_id}
                      className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer text-sm ${
                        checked ? "bg-tbc-cream/30" : "hover:bg-gray-50"
                      }`}
                      data-testid={`batch-verify-row-${m.business_id}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleRow(m.business_id)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-tbc-navy truncate">{m.name}</span>
                          {m.city && m.state && (
                            <span className="text-xs text-gray-500">· {m.city}, {m.state}</span>
                          )}
                          {m.category && (
                            <span className="text-[10px] uppercase tracking-wider bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                              {m.category}
                            </span>
                          )}
                        </div>
                        {snippet(m) && (
                          <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{snippet(m)}</p>
                        )}
                        {m.matched_keywords?.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {m.matched_keywords.map((k) => (
                              <span
                                key={k}
                                className="text-[10px] px-1.5 py-0.5 rounded bg-tbc-gold/20 text-tbc-navy font-medium"
                              >
                                {k}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <a
                        href={`/business/${m.business_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-tbc-navy/70 hover:underline flex-none"
                      >
                        View
                      </a>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 border-t border-gray-100 pt-3">
          <Button
            type="button"
            variant="outline"
            onClick={close}
            disabled={verifying}
            data-testid="batch-verify-cancel-btn"
          >
            Cancel
          </Button>
          {matches === null || matches.length === 0 ? (
            <Button
              type="button"
              onClick={runPreview}
              disabled={loading || !keywords.length}
              className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
              data-testid="batch-verify-preview-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              Preview matches
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={runPreview}
                disabled={loading || verifying}
                data-testid="batch-verify-refresh-preview-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Re-run preview
              </Button>
              <Button
                type="button"
                onClick={confirmVerify}
                disabled={verifying || !selected.size}
                className="bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90"
                data-testid="batch-verify-confirm-btn"
              >
                {verifying ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ShieldCheck className="w-4 h-4 mr-2" />
                )}
                Verify {selected.size} selected
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
